import {
  type CrewNote,
  type CrewNoteList,
  type PersonnelSource,
  type RoleList,
  rankChoices,
} from '@felix/ui';
import { api, officeApi } from './api.js';
import { crewMedical, saveCrewMedical, setCrewPhoto } from './crew-photo.js';
import { officeBrand } from './drills-source.js';
import { officeSetupSource } from './setup-source.js';

/** Ofis personel verisi (ofis API'si; M6d3): ortak ekranlar bu kaynakla çalışır. */
export const officeSource: PersonnelSource = {
  scope: 'web',
  vessels: () => officeApi.vessels(),
  crew: () => officeApi.crew(),
  crewProfile: (userId) => officeApi.crewProfile(userId),
  // Personel kartı yalnız ofiste yazılır (ADR 0131 §1)
  crewCards: {
    create: (input) => officeApi.createCrewCard(input),
    update: (userId, input) => officeApi.updateCrewCard(userId, input),
    createLogin: (userId, email, password) => officeApi.createCrewLogin(userId, email, password),
    // "Remove card" (ADR 0142 §6)
    remove: (userId, rowVersion, password) =>
      officeApi.removeCrewCard(userId, rowVersion, password),
    // Kartta "Reset password" (ortak 2026-10-30): Settings ▸ Users ile aynı uç
    resetPassword: (userId, rowVersion, password) =>
      officeSetupSource.resetPassword(userId, rowVersion, password),
    // Kartta hesap işlemleri (ortak 2026-10-30; ADR 0145): Settings ▸ Users ile aynı uçlar
    changeEmail: (userId, rowVersion, email, password) =>
      officeSetupSource.changeEmail(userId, rowVersion, email, password),
    setLoginActive: (userId, rowVersion, active, password) =>
      active
        ? officeSetupSource.enableUser(userId, rowVersion, password)
        : officeSetupSource.disableUser(userId, rowVersion, password),
    resetTotp: (userId, password) => officeSetupSource.resetTotp(userId, password),
    // Fotoğraf ve sağlık bilgisi (ADR 0158): fotoğraf tarayıcıda küçültülür, tus ile yüklenir
    setPhoto: (userId, rowVersion, file) => setCrewPhoto(userId, rowVersion, file),
    medical: (userId) => crewMedical(userId),
    saveMedical: (userId, input) => saveCrewMedical(userId, input),
  },
  // Joining / Sign-Off (ADR 0131 §3, Ek 2): liste; yazma yalnız ofiste
  movements: (query) => officeApi.crewMovements(query),
  movementEditor: {
    // Rütbe vessel + any (ADR 0143 §4); yönetici olmayana yönetici rolü yok (ADR 0151 §4)
    ranks: () => api<RoleList>('/office/roles').then(rankChoices),
    signOn: (input) => officeApi.signOn(input),
    signOff: (id, input) => officeApi.crewMovement(id, 'sign-off', input),
    rankChange: (id, input) => officeApi.crewMovement(id, 'rank-change', input),
    amend: (id, input) => officeApi.crewMovement(id, 'amend', input),
    cancel: (id, input) => officeApi.crewMovement(id, 'cancel', input),
    revoke: (id, input) => officeApi.crewMovement(id, 'revoke-sign-off', input),
    events: (id) => officeApi.movementEvents(id),
  },
  // Crew Profile ▸ Office notes (ADR 0159): yalnız ofiste; gemi kaynağında yok
  crewNotes: {
    list: (userId) =>
      api<CrewNoteList>(`/office/crew/profiles/${encodeURIComponent(userId)}/notes`),
    create: (userId, body) =>
      api<CrewNote>(`/office/crew/profiles/${encodeURIComponent(userId)}/notes`, {
        method: 'POST',
        body,
      }),
    correct: (noteId, expectedVersion, body) =>
      api<CrewNote>(`/office/crew/notes/${encodeURIComponent(noteId)}/correct`, {
        method: 'POST',
        body: { ...body, expectedVersion },
      }),
    withdraw: (noteId, expectedVersion, reason) =>
      api<CrewNote>(`/office/crew/notes/${encodeURIComponent(noteId)}/withdraw`, {
        method: 'POST',
        body: { expectedVersion, reason },
      }),
  },
  crewCertificates: (userId) => officeApi.crewCertificates(userId),
  restHours: (vesselId, from, to) => officeApi.restHours(vesselId, from, to),
  workPeriods: (vesselId, userId, from, to) => officeApi.workPeriods(vesselId, userId, from, to),
  restMonth: (vesselId, userId, month) => officeApi.restMonth(vesselId, userId, month),
  // Crew Profile ve Record of Employment başlığının markası (ADR 0131 Ek 3)
  brand: officeBrand,
};
